import { useState } from 'react';
import { Menu, X, LogOut, User as UserIcon, LogIn, LayoutDashboard, Shield, Megaphone, Briefcase, UserCheck, ClipboardList } from 'lucide-react';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from './ui/dropdown-menu';
import darkLogo from 'figma:asset/d1fe1a9231ec0614e594fed0b7cebc9d8b920db6.png';
import { brandSettings } from '../config/brand-config';
import { authService, User, UserRole } from '../lib/auth';

interface HeaderProps {
  currentPage: string;
  onNavigate: (page: any) => void;
  currentUser: User | null;
  onLogout: () => void;
  onOpenLogin: () => void;
}

export function Header({ currentPage, onNavigate, currentUser, onLogout, onOpenLogin }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Home', id: 'home' },
    { name: 'Properties', id: 'catalog' },
    { name: 'Agents', id: 'agent-directory' },
    { name: 'Services', id: 'services' },
    { name: 'Pre-Approval', id: 'pre-approval' },
    { name: 'About', id: 'about' },
    { name: 'Calculators', id: 'calculators' },
    { name: 'Blog', id: 'blog' },
    { name: 'Contact', id: 'contact' },
  ];

  // Role-based portal tabs
  const getRoleBasedTabs = (): Array<{ name: string; id: string; role: UserRole; icon: React.ReactNode }> => {
    const tabs: Array<{ name: string; id: string; role: UserRole; icon: React.ReactNode }> = [];
    
    if (currentUser?.role === 'agent') {
      tabs.push({ name: 'Agent Hub', id: 'hub', role: 'agent' as UserRole, icon: <Briefcase className="w-4 h-4" /> });
    }
    
    if (currentUser?.role === 'seller') {
      tabs.push({ name: 'Seller Portal', id: 'seller', role: 'seller' as UserRole, icon: <UserCheck className="w-4 h-4" /> });
    }
    
    if (currentUser?.role === 'external-agent') {
      tabs.push({ name: 'External Agent', id: 'external-agent', role: 'external-agent' as UserRole, icon: <LayoutDashboard className="w-4 h-4" /> });
    }
    
    if (currentUser?.role === 'admin') {
      tabs.push({ name: 'Admin Hub', id: 'admin', role: 'admin' as UserRole, icon: <Shield className="w-4 h-4" /> });
    }
    
    if (currentUser?.role === 'marketing') {
      tabs.push({ name: 'Marketing Hub', id: 'marketing', role: 'marketing' as UserRole, icon: <Megaphone className="w-4 h-4" /> });
    }

    if (currentUser?.role === 'deal-coordinator') {
      tabs.push({ name: 'DealDesk', id: 'dealdesk', role: 'deal-coordinator' as UserRole, icon: <ClipboardList className="w-4 h-4" /> });
    }

    return tabs;
  };

  const roleBasedTabs = getRoleBasedTabs();


  // Use custom logo if available, otherwise use default
  const logoSrc = brandSettings.logo || darkLogo;

  return (
    <header className="fixed top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button onClick={() => onNavigate('home')} className="flex items-center space-x-3 group" data-testid="header-logo">
              <img
                src={logoSrc}
                alt={`${brandSettings.companyName} Logo`}
                className="h-10 w-auto group-hover:scale-105 transition-transform duration-200"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1">
            {navigation.map((item) => (
              <Button
                key={item.id}
                variant={currentPage === item.id ? "default" : "ghost"}
                className={currentPage === item.id ? "bg-primary/10 text-primary hover:bg-primary/20" : "hover:bg-muted/50"}
                onClick={() => {
                  if (item.id === 'home') {
                    onNavigate('home');
                  } else if (item.id === 'catalog') {
                    onNavigate('catalog');
                  } else if (item.id === 'services') {
                    onNavigate('services');
                  } else if (item.id === 'agent-directory') {
                    onNavigate('agent-directory');
                  } else if (item.id === 'pre-approval') {
                    onNavigate('pre-approval');
                  } else if (item.id === 'about') {
                    onNavigate('about');
                  } else if (item.id === 'calculators') {
                    onNavigate('calculators');
                  } else if (item.id === 'blog') {
                    onNavigate('blog');
                  } else if (item.id === 'contact') {
                    onNavigate('contact');
                  }
                }}
                data-testid={`header-nav-${item.id}`}
              >
                {item.name}
              </Button>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-2">

            {/* Portal Login Button - Only show when not logged in */}
            {!currentUser && (
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:flex border-border/50 hover:border-primary/50 hover:bg-primary/5"
                onClick={onOpenLogin}
                data-testid="header-portal-login"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Portal Login
              </Button>
            )}

            {/* Role-based portal tabs - Only show for logged in users */}
            {roleBasedTabs.map((tab) => (
              <Button
                key={tab.id}
                variant={currentPage === tab.id ? 'default' : 'outline'}
                size="sm"
                className={
                  currentPage === tab.id
                    ? "hidden sm:inline-flex flex-shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
                    : "hidden sm:inline-flex flex-shrink-0 border-primary/40 text-primary hover:bg-primary/10 hover:border-primary/60 gap-1.5"
                }
                onClick={() => onNavigate(tab.id)}
                data-testid={`header-portal-${tab.id}`}
              >
                {tab.icon}
                <span className="hidden lg:inline">{tab.name}</span>
                <span className="lg:hidden">{tab.name.split(' ')[0]}</span>
              </Button>
            ))}

            {/* User Menu / Logout */}
            {currentUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="hidden sm:flex items-center gap-2 border-border/50 hover:border-primary/50" data-testid="header-user-menu">
                    <UserIcon className="w-4 h-4" />
                    <span className="hidden lg:inline">{currentUser.name}</span>
                    {currentUser.role && currentUser.role !== 'public' ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-semibold">
                        {currentUser.role.replace('-', ' ')}
                      </span>
                    ) : currentUser.role === 'public' ? (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-yellow-500/15 text-yellow-500 font-semibold">
                        no role
                      </span>
                    ) : null}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card/95 backdrop-blur-xl border-border/50">
                  <div className="px-2 py-1.5 text-sm font-medium">
                    {currentUser.name}
                  </div>
                  <div className="px-2 py-1 text-xs text-muted-foreground">
                    {currentUser.email}
                  </div>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1 text-xs text-muted-foreground capitalize">
                    Role: {currentUser.role.replace('-', ' ')}
                  </div>
                  <DropdownMenuSeparator />
                  {/* Quick link to their hub/portal */}
                  {roleBasedTabs.map((tab) => (
                    <DropdownMenuItem key={tab.id} onClick={() => onNavigate(tab.id)}>
                      {tab.icon}
                      <span className="ml-2">{tab.name}</span>
                    </DropdownMenuItem>
                  ))}
                  {roleBasedTabs.length > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={onLogout} data-testid="header-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden px-2 hover:bg-muted/50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="header-mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-border/50">
            <nav className="py-2 space-y-1">
              {navigation.map((item) => (
                <Button
                  key={item.id}
                  variant={currentPage === item.id ? "default" : "ghost"}
                  className={currentPage === item.id ? "w-full justify-start bg-primary/10 text-primary hover:bg-primary/20" : "w-full justify-start hover:bg-muted/50"}
                  onClick={() => {
                    if (item.id === 'home') {
                      onNavigate('home');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'catalog') {
                      onNavigate('catalog');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'services') {
                      onNavigate('services');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'agent-directory') {
                      onNavigate('agent-directory');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'pre-approval') {
                      onNavigate('pre-approval');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'about') {
                      onNavigate('about');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'calculators') {
                      onNavigate('calculators');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'blog') {
                      onNavigate('blog');
                      setIsMobileMenuOpen(false);
                    } else if (item.id === 'contact') {
                      onNavigate('contact');
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  data-testid={`header-mobile-nav-${item.id}`}
                >
                  {item.name}
                </Button>
              ))}
              
              {/* Role-based tabs in mobile menu */}
              {roleBasedTabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant="outline"
                  className="w-full justify-start mt-2 border-border/50 hover:border-primary/50 hover:bg-primary/5"
                  onClick={() => {
                    onNavigate(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  data-testid={`header-mobile-portal-${tab.id}`}
                >
                  {tab.icon}
                  {tab.name}
                </Button>
              ))}

              {/* Logout button in mobile menu */}
              {currentUser && (
                <>
                  <div className="px-4 py-2 mt-2 border-t border-border/50">
                    <div className="text-sm font-medium">{currentUser.name}</div>
                    <div className="text-xs text-muted-foreground">{currentUser.email}</div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full justify-start border-border/50 hover:border-destructive/50 hover:bg-destructive/5 text-destructive"
                    onClick={() => {
                      onLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    data-testid="header-mobile-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </>
              )}

              {/* Login button in mobile menu */}
              {!currentUser && (
                <div className="pt-2 mt-2 border-t border-border/50">
                  <Button
                    variant="outline"
                    className="w-full justify-start border-border/50 hover:border-primary/50 hover:bg-primary/5"
                    onClick={() => {
                      onOpenLogin();
                      setIsMobileMenuOpen(false);
                    }}
                    data-testid="header-mobile-portal-login"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Portal Login
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}